import { ExportJobState } from 'common-models';
import { useCallback, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useCreateExportJobMutation, useLazyGetExportJobQuery } from '../../../features/Admin/adminExportsApiSlice';



function triggerZipDownload(url: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = 'export.zip';
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
}

export interface ExportState {
  selectedEventIds: string[];
  currentExportJobId: string | null;
  exportJobStatus: ExportJobState | null;
  exportJobTotalImages: number;
  exportJobProcessedImages: number;
  exportInProgress: boolean;
  exportError: boolean;
}

export interface ExportActions {
  toggleEventSelection: (eventId: string) => void;
  clearSelection: () => void;
  selectAll: (eventIds: string[]) => void;
  startExport: () => Promise<void>;
}

export const useExportManager = (): [ExportState, ExportActions] => {
  const auth = useAuth();
  const [createExportJob] = useCreateExportJobMutation();
  const [getExportJob] = useLazyGetExportJobQuery();

  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
  const [currentExportJobId, setCurrentExportJobId] = useState<string | null>(null);
  const [exportJobStatus, setExportJobStatus] = useState<ExportJobState | null>(null);
  const [exportJobTotalImages, setExportJobTotalImages] = useState(0);
  const [exportJobProcessedImages, setExportJobProcessedImages] = useState(0);
  const [exportInProgress, setExportInProgress] = useState(false);
  const [exportError, setExportError] = useState(false);

  const accessToken = auth.user?.access_token ?? '';

  const pollExportJobStatus = useCallback(
    (jobId: string) => {
      let pollingActive = true;

      const poll = async () => {
        if (!pollingActive) return;

        try {
          const response = await getExportJob({ jobId, accessToken });

          if (response.isSuccess && response.data) {
            const job = response.data;
            setExportJobStatus(job.state);
            setExportJobTotalImages(job.totalImageCount);
            setExportJobProcessedImages(job.imagesProcessedCount);

            if (job.state === 'completed') {
              pollingActive = false;
              if (job.presignedUrl) {
                triggerZipDownload(job.presignedUrl);
              }
              setCurrentExportJobId(null);
              setExportInProgress(false);
              setTimeout(() => {
                setExportJobStatus(null);
              }, 3000);
            } else if (job.state === 'failed') {
              pollingActive = false;
              setExportError(true);
              setCurrentExportJobId(null);
              setExportInProgress(false);
              setTimeout(() => {
                setExportJobStatus(null);
                setExportError(false);
              }, 5000);
            } else {
              setTimeout(poll, 1000);
            }
          } else {
            setTimeout(poll, 1000);
          }
        } catch {
          setTimeout(poll, 1000);
        }
      };

      poll();
      return () => {
        pollingActive = false;
      };
    },
    [getExportJob, accessToken]
  );

  const toggleEventSelection = useCallback((eventId: string) => {
    setSelectedEventIds((prev) => {
      if (prev.includes(eventId)) {
        return prev.filter((id) => id !== eventId);
      }
      return [...prev, eventId];
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedEventIds([]);
  }, []);

  const selectAll = useCallback((eventIds: string[]) => {
    setSelectedEventIds(eventIds);
  }, []);

  const startExport = useCallback(async () => {
    if (selectedEventIds.length === 0) return;

    setExportInProgress(true);
    setExportError(false);

    try {
      const exportJob = await createExportJob({
        eventIds: selectedEventIds,
        accessToken,
      }).unwrap();

      if (exportJob?.id) {
        setCurrentExportJobId(exportJob.id);
        setExportJobStatus(exportJob.state);
        pollExportJobStatus(exportJob.id);
      }
    } catch {
      setExportInProgress(false);
      setExportError(true);
      setTimeout(() => {
        setExportError(false);
      }, 5000);
    }
  }, [selectedEventIds, accessToken, createExportJob, pollExportJobStatus]);

  const state: ExportState = {
    selectedEventIds,
    currentExportJobId,
    exportJobStatus,
    exportJobTotalImages,
    exportJobProcessedImages,
    exportInProgress,
    exportError,
  };

  const actions: ExportActions = {
    toggleEventSelection,
    clearSelection,
    selectAll,
    startExport,
  };

  return [state, actions];
};
